import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { differenceInDays, differenceInMonths, addMonths, isSameDay } from 'date-fns'; // Používáme knihovnu `date-fns` pro snadný výpočet rozdílu dnů
import { DateRangeFormControl } from './datepicker-range.component';
import { parseStringToDate } from '@shared/utils';

// Vlastní validátor pro kontrolu, zda je alespoň jedno pole vyplněné
export function atLeastOneFieldRequiredValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const dateRangeControl = control as DateRangeFormControl;

    // Přístup k `fromKey` a `toKey`
    if (dateRangeControl.fromKey && dateRangeControl.toKey) {
      const fromKey = dateRangeControl.fromKey;
      const toKey = dateRangeControl.toKey;

      // Přístup k hodnotám v `control.value` na základě `fromKey` a `toKey`
      const value = control.value as Record<string, Date | null>;

      // Pokud je `value` null, vracíme chybu (není vyplněno ani jedno datum)
      if (value === null) {
        return { atLeastOneDateRequired: true };
      }

      // Ověří, zda alespoň jedno z polí obsahuje hodnotu
      const isValid = value[fromKey] !== null || value[toKey] !== null;

      // Vrátí chybu, pokud jsou obě hodnoty `null`
      const rtn = isValid ? null : { atLeastOneDateRequired: true };

      return rtn;
    }
    return null;
  };
}

/**
 * Validátor pro ui-datepicker-range:
 *  – pole “from” musí být ≥ minDate
 *  – pole “to”  musí být ≤ maxDate
 * Podporuje hodnoty typu Date i string.
 */
export function datepickerRangeBoundsValidator(minDate: Date | null, maxDate: Date | null): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const drc = control as DateRangeFormControl;
    if (!drc.fromKey || !drc.toKey || control.value == null) {
      return null;
    }

    const raw = control.value as Record<string, string | Date | null>;
    const parse = (v: string | Date | null): Date | null =>
      v instanceof Date ? v : typeof v === 'string' ? parseStringToDate(v) : null;

    const fromDate = parse(raw[drc.fromKey]);
    const toDate = parse(raw[drc.toKey]);

    const errors: ValidationErrors = {};

    // from < minDate
    if (minDate && fromDate && fromDate < minDate) {
      errors['fromBeforeMinDatepickerRange'] = {
        requiredMin: minDate,
        actual: fromDate,
        isMinToday: isSameDay(minDate, new Date()),
      };
    }

    // to > maxDate
    if (maxDate && toDate && toDate > maxDate) {
      errors['toAfterMaxDatepickerRange'] = {
        requiredMax: maxDate,
        actual: toDate,
        isMaxToday: isSameDay(maxDate, new Date()),
      };
    }

    return Object.keys(errors).length ? errors : null;
  };
}

export type DateRangeUnit = 'days' | 'months' | 'years';

export function maxDateRangeValidator(maxRange: number, unit: DateRangeUnit = 'days'): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const dateRangeControl = control as DateRangeFormControl;

    if (dateRangeControl.fromKey && dateRangeControl.toKey) {
      const fromKey = dateRangeControl.fromKey;
      const toKey = dateRangeControl.toKey;

      const value = control.value as Record<string, Date | null>;

      // Pokud je `control.value` nebo jedno z dat null, validace neprojde
      if (!value || !value[fromKey] || !value[toKey]) {
        return {
          dateRangeInvalid: true,
        };
      }

      const startDate = new Date(value[fromKey] as Date);
      const endDate = new Date(value[toKey] as Date);

      let rangeDiff: number;

      switch (unit) {
        case 'months': {
          const fullMonths = differenceInMonths(endDate, startDate); // Celé měsíce
          const adjustedStartDate = addMonths(startDate, fullMonths); // Datum po přičtení celých měsíců
          const remainingDays = differenceInDays(endDate, adjustedStartDate); // Zbylé dny
          rangeDiff = fullMonths + remainingDays / 30; // Přesný rozdíl (měsíce + frakce měsíců)
          break;
        }
        case 'years': {
          const fullYears = Math.floor(differenceInMonths(endDate, startDate) / 12); // Celé roky
          const adjustedStartDate = addMonths(startDate, fullYears * 12); // Datum po přičtení celých roků
          const remainingMonths = differenceInMonths(endDate, adjustedStartDate); // Zbylé měsíce
          const adjustedStartForDays = addMonths(adjustedStartDate, remainingMonths);
          const remainingDays = differenceInDays(endDate, adjustedStartForDays);
          rangeDiff = fullYears + remainingMonths / 12 + remainingDays / 365;
          break;
        }
        default:
          rangeDiff = differenceInDays(endDate, startDate); // Počet dní
          break;
      }

      // Pokud rozdíl překračuje zadaný limit
      if (rangeDiff > maxRange) {
        return {
          dateRangeExceedsMaxRange: {
            maxRange,
            unit,
            actualRange: rangeDiff.toFixed(2), // Vrací přesný rozdíl
          },
        };
      }

      return null; // Validace prošla
    }

    return null;
  };
}

export function dateFromGreaterThanToValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const dateRangeControl = control as DateRangeFormControl;

    if (dateRangeControl && dateRangeControl.fromKey && dateRangeControl.toKey) {
      const fromKey = dateRangeControl.fromKey;
      const toKey = dateRangeControl.toKey;

      // Ověříme, že `value` není null nebo undefined
      if (!dateRangeControl.value) {
        return null; // Pokud není hodnota k dispozici, validátor neprovádí žádnou validaci
      }

      const fromValue = dateRangeControl.value[fromKey];
      const toValue = dateRangeControl.value[toKey];

      if (fromValue && toValue) {
        const fromDate = new Date(fromValue as Date);
        const toDate = new Date(toValue as Date);

        if (fromDate > toDate) {
          // Pokud je datum "od" větší než "do", vrátíme chybu
          // msg >> Datum 'od' musí být menší než datum 'do'
          return { dateFromGreaterThanDateTo: true };
        }
      }
    }
    return null; // Pokud je rozsah správný nebo data nejsou zadána, vracíme null
  };
}
